import { useEffect, useMemo, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, Divider, FormLabel, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DialogActions from '@mui/material/DialogActions';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import StatusApiService from '../../../api/StatusApiService';
import { StatusDto } from '../../../dtos/Statuses/StatusDto';
import SaveButton from '../../Common/SaveButton';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import { useSnackbar } from '../../Common/SnackbarProvider';

const SOURCE_LOCALE = 'en';
const TRANSLATABLE_LOCALES = [
    { code: 'ru', label: 'Русский' },
    { code: 'fr', label: 'Français' },
];

export default function ModifyStatusModal(props) {
    const { onClose, open, selectedStatus, statusApiService, onCancel } = props;
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [statusName, setStatusName] = useState('');
    const [sortSequence, setSortSequence] = useState(0);
    const [translations, setTranslations] = useState({});
    const [manualLocales, setManualLocales] = useState([]);

    const [nameError, setNameError] = useState('');
    const [sortSequenceError, setSortSequenceError] = useState('');

    const isEdit = Boolean(selectedStatus?.stId);

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        if (!validateInfo()) return;

        const statusDto = new StatusDto();
        statusDto.stId = selectedStatus?.stId ?? 0;
        statusDto.stName = statusName;
        statusDto.stSortSeq = sortSequence;
        statusDto.names = { [SOURCE_LOCALE]: statusName, ...translations };
        statusDto.manualLocales = manualLocales;

        const result = isEdit
            ? await statusApiService.updateStatus(statusDto)
            : await statusApiService.createStatus(statusDto);

        if (result?.success) {
            snackbar.success(t(isEdit ? 'modifyStatus.toast.updated' : 'modifyStatus.toast.added'));
            resetModal();
        } else {
            snackbar.error(t(isEdit ? 'modifyStatus.toast.updateError' : 'modifyStatus.toast.addError', { error: result?.message }));
        }
    });

    function validateInfo() {
        const errors = [];

        if(statusName === null || statusName === '') {
            errors.push(t('modifyStatus.validation.nameRequired'));
        }

        setNameError(!statusName);

        if(sortSequence < 1) {
            errors.push(t('common.validation.sortSequence'));
        }

        setSortSequenceError(sortSequence < 1);

        if(errors.length > 0) {
            snackbar.error(t('modifyStatus.validation.failed', { errors: errors.join(', ') }));
            return false;
        } else {
            return true;
        }
    }

    function resetModal() {
        setSortSequence(0);
        setStatusName('');
        setTranslations({});
        setManualLocales([]);
        onClose();
    }

    function onTranslationChange(locale, value) {
        setTranslations(prev => ({ ...prev, [locale]: value }));
        if (!manualLocales.includes(locale)) {
            setManualLocales(prev => [...prev, locale]);
        }
    }

    function onClearOverride(locale) {
        // Removing the manual flag + clearing the value tells the server to regenerate on save.
        setTranslations(prev => {
            const next = { ...prev };
            delete next[locale];
            return next;
        });
        setManualLocales(prev => prev.filter(l => l !== locale));
    }

    const helperText = useMemo(() => isEdit
        ? t('modifyStatus.translations.helperEdit')
        : t('modifyStatus.translations.helperCreate'), [isEdit, t]);

    useEffect(() => {
        if(open) {
            setStatusName(selectedStatus?.stName ?? '');
            setSortSequence(selectedStatus?.stSortSeq ?? 0);
            const incomingNames = selectedStatus?.names ?? {};
            const nonSource = Object.fromEntries(
                Object.entries(incomingNames).filter(([locale]) => locale !== SOURCE_LOCALE)
            );
            setTranslations(nonSource);
            setManualLocales(selectedStatus?.manualLocales ?? []);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedStatus, open])

    return(
        <Dialog
            open={open}
            sx={{ '& .MuiDialog-paper': { width: '100%' } }}
            maxWidth={'lg'}
            fullWidth
        >
            <DialogTitle>{selectedStatus?.stName ? t('modifyStatus.title.edit', { name: selectedStatus.stName }) : t('modifyStatus.title.create')}</DialogTitle>
            <DialogContent dividers>
                <Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('modifyStatus.field.name')}</FormLabel>
                        <br/>
                        <TextField
                            error={nameError}
                            sx={{ width: '100%' }}
                            autoComplete={'Status Name'}
                            value={statusName}
                            onChange={(event) => setStatusName(event.target.value)}
                        />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                        <FormLabel>{t('fieldName.sortSequence')}</FormLabel>
                        <br/>
                        <TextField
                            error={sortSequenceError}
                            sx={{ width: '100%' }}
                            type={'number'}
                            autoComplete={'Sort Sequence'}
                            value={sortSequence}
                            onChange={(event) => setSortSequence(event.target.value)}
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" fontWeight={600}>
                        {t('modifyStatus.translations.title')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {helperText}
                    </Typography>
                    <Stack spacing={1.5}>
                        {TRANSLATABLE_LOCALES.map(({ code, label }) => {
                            const isManual = manualLocales.includes(code);
                            return (
                                <Box key={code} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <FormLabel sx={{ minWidth: 90 }}>{label}</FormLabel>
                                    <TextField
                                        size="small"
                                        sx={{ flex: 1 }}
                                        value={translations[code] ?? ''}
                                        placeholder={isEdit ? '' : t('modifyStatus.translations.willGenerate')}
                                        onChange={(e) => onTranslationChange(code, e.target.value)}
                                        InputProps={{
                                            endAdornment: isManual ? (
                                                <Tooltip title={t('modifyStatus.translations.regenerateTooltip')}>
                                                    <IconButton size="small" onClick={() => onClearOverride(code)}>
                                                        <RefreshRoundedIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null,
                                        }}
                                        helperText={isManual ? t('modifyStatus.translations.manualBadge') : t('modifyStatus.translations.autoBadge')}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button autoFocus variant={'contained'} onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
                <SaveButton loading={saving} variant={'contained'} onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}

ModifyStatusModal.propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    selectedStatus: PropTypes.objectOf(StatusDto),
    statusApiService: PropTypes.objectOf(StatusApiService).isRequired,
    onCancel: PropTypes.func.isRequired,
}
